// src/features/text/case-converter/CaseConverter.tsx
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, ButtonGroup, Typography } from '@mui/material';
import TextToolWorkspace from '../components/TextToolWorkspace';
import StyledFAQ from '../../common/components/StyledFAQ';
import * as textUtils from '../utils/textUtils';

type CaseType = 'upper' | 'lower' | 'capitalized' | 'sentence' | 'inverted';

// Configuration for case conversion options
const caseOptions: { id: CaseType; nameKey: string; transform: (text: string) => string }[] = [
    { id: 'upper', nameKey: 'text_case_converter_upper', transform: textUtils.toUpperCase },
    { id: 'lower', nameKey: 'text_case_converter_lower', transform: textUtils.toLowerCase },
    { id: 'capitalized', nameKey: 'text_case_converter_capitalized', transform: textUtils.toCapitalizedCase },
    { id: 'sentence', nameKey: 'text_case_converter_sentence', transform: textUtils.toSentenceCase },
    { id: 'inverted', nameKey: 'text_case_converter_inverted', transform: textUtils.toInvertedCase },
];

const CaseConverter: React.FC = () => {
    const { t } = useTranslation('tools');
    const [inputText, setInputText] = useState<string>('');
    const [activeCase, setActiveCase] = useState<CaseType | null>(null);

    const outputText = useMemo(() => {
        if (!activeCase || !inputText) return '';
        const option = caseOptions.find(opt => opt.id === activeCase);
        return option ? option.transform(inputText) : '';
    }, [inputText, activeCase]);

    const handleConvert = (caseType: CaseType) => {
        setActiveCase(caseType);
        // The outputText will automatically update via useMemo
    };

    const faqs = [
        { q: 'text_case_converter_faq1_q', a: 'text_case_converter_faq1_a' },
        { q: 'text_case_converter_faq2_q', a: 'text_case_converter_faq2_a' },
        { q: 'text_case_converter_faq3_q', a: 'text_case_converter_faq3_a' },
    ];

    return (
        <Box>
            <TextToolWorkspace
                inputText={inputText}
                outputText={outputText}
                onInputChange={setInputText}
                inputLabelKey="text_common_input_label"
                outputLabelKey="text_common_output_label"
            >
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
                    {t('text_case_converter_options_title')}
                </Typography>
                <ButtonGroup variant="outlined" aria-label="case conversion options"_>
                    {caseOptions.map((option) => (
                        <Button
                            key={option.id}
                            onClick={() => handleConvert(option.id)}
                            variant={activeCase === option.id ? 'contained' : 'outlined'}
                        >
                            {t(option.nameKey)}
                        </Button>
                    ))}
                </ButtonGroup>
            </TextToolWorkspace>

            <Box sx={{ my: 8 }}>
                <Typography variant="h4" component="h2" gutterBottom textAlign="center">
                    {t('text_case_converter_about_title')}
                </Typography>
                <Typography paragraph color="text.secondary" textAlign="center" maxWidth="md" mx="auto">
                    {t('text_case_converter_about_desc')}
                </Typography>
                <StyledFAQ
                    title={t('faq_title')}
                    faqs={faqs}
                    t={t}
                />
            </Box>
        </Box>
    );
};

export default CaseConverter;